import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchFromApi } from 'api/apiClient';
import { SortOrder } from 'hooks/useTableControls';
import { PaginationQueryParams } from 'types/queryTypes';
import { AssignableRolesResponse, Role, RoleDetail } from 'types/rolesTypes';

const ROLES_QUERY_KEY = 'roles';
const ROLE_QUERY_KEY = 'get_role';
const REACT_APP_DARWIN_URL = import.meta.env.VITE_DARWIN_URL;

/**
 * Fetch roles.
 */
export const useGetRoles = ({
  companyId,
  page = 1,
  limit = 10,
  query = '',
  sortOrder = { sortBy: 'id', order: 'ASC' },
}: PaginationQueryParams & { companyId: string | number } & {
  sortOrder: SortOrder;
}) =>
  useQuery({
    queryKey: [ROLES_QUERY_KEY, limit, page, query, sortOrder],
    queryFn: () =>
      fetchFromApi<Role[]>(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/permissions/roles?limit=${limit}&page=${page}&filter=${query}&sortBy=${sortOrder.sortBy}&order=${sortOrder.order}`
      ),
    refetchOnWindowFocus: false,
  });

/**
 * Fetch role detail.
 */
export const useGetRoleDetail = ({
  companyId,
  roleId,
}: {
  companyId: string;
  roleId: string;
}) =>
  useQuery({
    queryKey: [ROLES_QUERY_KEY, roleId],
    queryFn: () =>
      fetchFromApi<RoleDetail>(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/permissions/roles/${roleId}`
      ),
  });

/**
 * Create a role.
 */
export const useCreateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, role }: { companyId: string; role: any }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/permissions/roles`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(role),
        }
      ),
    onSuccess: () => {
      // Invalidate the cache of the role list after creating an role
      queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] });
    },
  });
};

/**
 * Update a role.
 */
export const useUpdateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ companyId, role }: { companyId: string; role: any }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/permissions/roles/${role.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(role),
        }
      ),
    onSuccess: () => {
      // Invalidate the cache of the role list after updating an role
      queryClient.invalidateQueries({
        queryKey: [ROLES_QUERY_KEY, ROLE_QUERY_KEY],
      });
    },
  });
};

/**
 * Delete a role.
 */
export const useDeleteRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      roleId,
    }: {
      companyId: string;
      roleId: string;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/permissions/roles/${roleId}`,
        {
          method: 'DELETE',
        }
      ),
    onSuccess: () => {
      // Invalidate the cache of the role list after deleting an role
      queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] });
    },
  });
};

/**
 * Fetch all assignable roles for company.
 */
export const useGetAssignableRolesForCompany = (companyId: string | number) =>
  useQuery({
    queryKey: [ROLES_QUERY_KEY, companyId],
    queryFn: () =>
      fetchFromApi<AssignableRolesResponse[]>(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/permissions/roles/selector`
      ),
    refetchOnWindowFocus: false,
  });
